/* ============================================================================
   olmia-editor.js — theme-editor re-init layer. Loaded once (head, defer) so it
   runs BEFORE every section's own defer script and can offer window.OlmiaEditor
   for those scripts to register against.

   THE PROBLEM IT SOLVES
   ---------------------
   The Shopify theme editor re-renders a single section by replacing its
   #shopify-section-<id> markup with freshly-rendered HTML and dispatching
   `shopify:section:load`. The section's inline <script src> does NOT re-execute
   (innerHTML-inserted scripts never run), so every DOMContentLoaded-bound
   behaviour (carousel, filters, reveals, builders, header) goes dead on the new
   nodes — and any globally-latched flag (html.gsap-ready, announceStarted) keeps
   it dead. This module re-drives the right init on the re-rendered root.

   THE CONTRACT
   ------------
   Each interactive olmia-*.js registers exactly one entry:
     OlmiaEditor.register({ key, match(root)->bool, init(root), teardown(root),
                            blockSelect?(root, detail), blockDeselect?(root, detail) })
   Rules the modules follow (see each file):
     • init(root) scopes its queries to `root` (the section wrapper), is idempotent
       (re-running is safe), and rebinds section-internal listeners on the fresh
       nodes. document/window/timer/observer resources are bound ONCE at module
       scope and cleared in teardown so nothing leaks or double-fires.
     • teardown(root) clears timers / observers / window listeners for that section.

   On `shopify:section:load` we run teardown→init for every matching entry, then
   force the reveal (design mode shows instantly) and repaint prices, so the
   re-rendered section is alive and visible. Only active in Shopify.designMode.
   ========================================================================== */
(function () {
  var registry = [];
  var byKey = {};
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  function register(entry) {
    if (!entry || !entry.key) return entry;
    // Dedupe by key: if a section script somehow re-executes, replace its entry
    // rather than stacking a second one (which would double-init).
    if (byKey[entry.key]) {
      var i = registry.indexOf(byKey[entry.key]);
      if (i !== -1) registry.splice(i, 1, entry);
    } else {
      registry.push(entry);
    }
    byKey[entry.key] = entry;
    return entry;
  }

  function rootFrom(e) {
    var t = e.target;
    if (t && t.nodeType === 1 && typeof t.id === 'string' && t.id.indexOf('shopify-section-') === 0) return t;
    var id = e.detail && e.detail.sectionId;
    if (id) {
      // hardening (adversarial finding H1): only accept a raw-id fallback when it
      // actually is a section wrapper — never a page element like "siteHeader"
      var byPrefix = document.getElementById('shopify-section-' + id);
      if (byPrefix) return byPrefix;
      var raw = document.getElementById(id);
      return (raw && String(raw.id).indexOf('shopify-section-') === 0) ? raw : null;
    }
    return (t && t.closest) ? t.closest('.shopify-section') : null;
  }

  function entriesFor(root) {
    return registry.filter(function (en) {
      try { return !!en.match(root); } catch (err) { return false; }
    });
  }

  function loadSection(root) {
    if (!root) return;
    entriesFor(root).forEach(function (en) {
      try { if (en.teardown) en.teardown(root); } catch (err) { console.error('[olmia-editor] teardown', en.key, err); }
      try { if (en.init) en.init(root); } catch (err) { console.error('[olmia-editor] init', en.key, err); }
    });
    // Reveal the re-rendered content (design mode = instant, never hidden) and
    // repaint any price / data-money spans the fresh markup carries.
    try { if (window.revealScope) window.revealScope(root); } catch (err) { console.error(err); }
    try { if (window.OlmiaUI && OlmiaUI.paintPrices) OlmiaUI.paintPrices(root); } catch (err) { console.error(err); }
    // Header reloaded → repaint the live cart/wishlist counts onto the fresh nodes.
    try {
      if (root.querySelector && root.querySelector('[data-cart-count]')) {
        if (window.OlmiaCart && window.OlmiaUI && OlmiaCart.cart) OlmiaCart.refresh();
        if (window.OlmiaWishlist && OlmiaWishlist.syncUI) OlmiaWishlist.syncUI();
      }
    } catch (err) { console.error(err); }
  }

  function unloadSection(root) {
    if (!root) return;
    entriesFor(root).forEach(function (en) {
      try { if (en.teardown) en.teardown(root); } catch (err) { console.error('[olmia-editor] unload', en.key, err); }
    });
  }

  function dispatchBlock(name, e) {
    var root = rootFrom(e);
    if (!root) return;
    entriesFor(root).forEach(function (en) {
      try { if (en[name]) en[name](root, e.detail || {}); } catch (err) { console.error('[olmia-editor] ' + name, en.key, err); }
    });
  }

  if (designMode) {
    // Belt-and-suspenders: stamp the class the always-emitted CSS guard keys on,
    // so [data-reveal] can never stay hidden in the editor even if JS mis-times.
    document.documentElement.classList.add('shopify-design-mode');
    document.addEventListener('shopify:section:load', function (e) { loadSection(rootFrom(e)); });
    document.addEventListener('shopify:section:unload', function (e) { unloadSection(rootFrom(e)); });
    document.addEventListener('shopify:block:select', function (e) { dispatchBlock('blockSelect', e); });
    document.addEventListener('shopify:block:deselect', function (e) { dispatchBlock('blockDeselect', e); });
  }

  window.OlmiaEditor = {
    register: register,
    loadSection: loadSection,
    unloadSection: unloadSection,
    designMode: designMode,
    _registry: registry
  };
})();
